/**
 * Mark the visible thread read (its newest message) a beat after it changes,
 * so the sidebar's unread bolding clears while the user is looking at the
 * channel without a request per streamed token. Skipped while the tab is
 * hidden: an unread channel should stay unread until it is actually seen.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";

import { AgentAPI } from "../api/agentApi";
import type { AgentScope } from "../types";

export const READ_DEBOUNCE_MS = 800;

/** `created_at_ns` of the newest message, if any carries one. */
export const latestMessageNs = (
  messages: ReadonlyArray<object>
): number | null => {
  let max: number | null = null;
  for (const m of messages) {
    const ns = (m as { created_at_ns?: unknown }).created_at_ns;
    if (typeof ns === "number" && (max === null || ns > max)) max = ns;
  }
  return max;
};

/**
 * The id of the newest message when it has no `created_at_ns` yet (a reply
 * this tab streamed) and no turn is running: read up to the server's newest.
 */
export const unstampedTailId = (
  messages: ReadonlyArray<{ id: string }>,
  settled: boolean
): string | null => {
  const last = messages[messages.length - 1];
  if (!settled || !last) return null;
  const ns = (last as { created_at_ns?: unknown }).created_at_ns;
  return typeof ns === "number" ? null : last.id;
};

export type UseThreadReadOptions = {
  scope: AgentScope | null;
  threadId: string | null;
  /** The newest message's `created_at_ns` (see `latestMessageNs`). */
  latestNs: number | null;
  /** See `unstampedTailId`. */
  tailId?: string | null;
  /** Off while the thread is not on screen (another tab of the view). */
  enabled?: boolean;
  /** Called after the server accepted the cursor (refresh the sidebar). */
  onRead?: () => void;
};

const onVisibility = (cb: () => void) => {
  document.addEventListener("visibilitychange", cb);
  return () => document.removeEventListener("visibilitychange", cb);
};
const pageVisible = () => document.visibilityState !== "hidden";

export const useThreadRead = ({
  scope,
  threadId,
  latestNs,
  tailId = null,
  enabled = true,
  onRead
}: UseThreadReadOptions): void => {
  // The last cursor we sent per thread, so a re-render never re-posts it.
  const sent = useRef<Map<string, number>>(new Map());
  // The unstamped tails already read, by thread.
  const sentTail = useRef<Map<string, string>>(new Map());
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  // A reply that lands while the tab is hidden is read when it comes back.
  const visible = useSyncExternalStore(onVisibility, pageVisible, () => true);

  useEffect(() => {
    if (!enabled || !scope || !threadId || !visible) return undefined;
    if (tailId) {
      if (sentTail.current.get(threadId) === tailId) return undefined;
      const t = setTimeout(() => {
        sentTail.current.set(threadId, tailId);
        AgentAPI.markRead(scope, threadId)
          .then(() => onReadRef.current?.())
          .catch(() => {
            if (sentTail.current.get(threadId) === tailId)
              sentTail.current.delete(threadId);
          });
      }, READ_DEBOUNCE_MS);
      return () => clearTimeout(t);
    }
    if (latestNs == null) return undefined;
    if ((sent.current.get(threadId) ?? -1) >= latestNs) return undefined;
    const t = setTimeout(() => {
      sent.current.set(threadId, latestNs);
      AgentAPI.markRead(scope, threadId, latestNs)
        .then(() => onReadRef.current?.())
        .catch(() => {
          // Retry on the next change.
          if (sent.current.get(threadId) === latestNs)
            sent.current.delete(threadId);
        });
    }, READ_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [enabled, scope, threadId, latestNs, tailId, visible]);
};
